
import { addDays, format, parseISO, isValid, startOfDay } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

/**
 * Timezone-aware date utilities to fix date shift issues
 */

/**
 * Create a date in a specific timezone without time shift
 */
export function createDateInTimezone(year: number, month: number, day: number, timezone: string): Date {
  try {
    // Create date string at noon in the target timezone to avoid midnight boundary issues
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T12:00:00`;
    
    // Convert to proper timezone-aware date
    const zonedDate = toZonedTime(new Date(dateString), timezone);
    
    console.log(`[DateUtils] Created date in ${timezone}:`, {
      input: `${year}-${month}-${day}`,
      dateString,
      zonedDate: zonedDate.toISOString(),
      dayOfWeek: zonedDate.getDay()
    });
    
    return zonedDate;
  } catch (error) {
    console.error('[DateUtils] Error creating date in timezone:', error);
    return new Date(year, month - 1, day, 12); // Fallback to noon
  }
}

/**
 * Generate dates safely without month boundary issues
 */
export function generateDateRange(startDate: Date, numberOfDays: number, timezone: string): Date[] {
  try {
    const dates: Date[] = [];
    let currentDate = startOfDay(toZonedTime(startDate, timezone));
    
    for (let i = 0; i < numberOfDays; i++) {
      dates.push(new Date(currentDate));
      currentDate = addDays(currentDate, 1);
    }
    
    return dates;
  } catch (error) {
    console.error('[DateUtils] Error generating date range:', error);
    return [];
  }
}

/**
 * Compare dates in the same timezone context
 */
export function isSameDayInTimezone(date1: Date, date2: Date, timezone: string): boolean {
  try {
    const zonedDate1 = toZonedTime(date1, timezone);
    const zonedDate2 = toZonedTime(date2, timezone);
    
    return (
      zonedDate1.getFullYear() === zonedDate2.getFullYear() &&
      zonedDate1.getMonth() === zonedDate2.getMonth() &&
      zonedDate1.getDate() === zonedDate2.getDate()
    );
  } catch (error) {
    console.error('[DateUtils] Error comparing dates in timezone:', error);
    return false;
  }
}

/**
 * Get date key in organizer's timezone
 * FIXED: Actually use timezone for proper date key generation
 */
export function getDateKeyInTimezone(date: Date, timezone: string): string {
  // Guard against undefined or invalid dates
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    console.warn('[getDateKeyInTimezone] Invalid date passed:', date);
    return new Date().toISOString().split('T')[0]; // Return today's date as fallback
  }

  try {
    // Convert date to the target timezone first, then get date components
    const zonedDate = toZonedTime(date, timezone);
    const year = zonedDate.getFullYear();
    const month = (zonedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = zonedDate.getDate().toString().padStart(2, '0');
    
    // Return the date key using timezone-converted date components
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('[DateUtils] Error getting date key in timezone:', error);
    return format(date, 'yyyy-MM-dd');
  }
}

/**
 * Check if date is today in specific timezone
 */
export function isTodayInTimezone(date: Date, timezone: string): boolean {
  try {
    const today = new Date();
    return isSameDayInTimezone(date, today, timezone);
  } catch (error) {
    console.error('[DateUtils] Error checking if date is today:', error);
    return false;
  }
}

/**
 * Format date for display in specific timezone
 */
export function formatDateInTimezone(date: Date, timezone: string, formatString: string = 'PPP'): string {
  try {
    return formatInTimeZone(date, timezone, formatString);
  } catch (error) {
    console.error('[DateUtils] Error formatting date in timezone:', error);
    return format(date, formatString);
  }
}

/**
 * Convert local date to UTC maintaining the same calendar day
 */
export function localDateToUTC(date: Date, timezone: string): Date {
  try {
    return fromZonedTime(date, timezone);
  } catch (error) {
    console.error('[DateUtils] Error converting local date to UTC:', error);
    return date;
  }
}

/**
 * Convert UTC date to local timezone
 */
export function utcDateToLocal(date: Date, timezone: string): Date {
  try {
    return toZonedTime(date, timezone);
  } catch (error) {
    console.error('[DateUtils] Error converting UTC date to local:', error);
    return date;
  }
}

/**
 * Create today's date in specific timezone
 */
export function getTodayInTimezone(timezone: string): Date {
  try {
    const now = new Date();
    const zonedNow = toZonedTime(now, timezone);
    return startOfDay(zonedNow);
  } catch (error) {
    console.error('[DateUtils] Error getting today in timezone:', error);
    return startOfDay(new Date());
  }
}

/**
 * Generate calendar dates for a month using timezone-aware creation
 */
export function generateCalendarDatesForMonth(year: number, month: number, timezone: string): Date[] {
  try {
    const dates: Date[] = [];
    const daysInMonth = new Date(year, month, 0).getDate(); // month is 1-indexed here
    
    console.log(`[DateUtils] Generating calendar dates for ${year}-${month} in ${timezone}`);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = createDateInTimezone(year, month, day, timezone);
      dates.push(date);
    }
    
    console.log(`[DateUtils] Generated ${dates.length} dates for month`, {
      firstDate: dates[0]?.toISOString(),
      lastDate: dates[dates.length - 1]?.toISOString(),
      timezone
    });
    
    return dates;
  } catch (error) {
    console.error('[DateUtils] Error generating calendar dates:', error);
    return [];
  }
}
